/**
 * Certificate utility functions for crew management
 * Handles categorization, status calculation, and formatting
 *
 * @module certificateUtils
 * @since Phase 5 - Certificates Tab Enhancement
 */

// ============================================================================
// CATEGORY DEFINITIONS
// ============================================================================

export const CERTIFICATE_CATEGORIES = {
  STCW: {
    id: 'stcw',
    label: 'STCW Certificates',
    icon: '🌊',
    patterns: [
      'stcw',
      'basic safety',
      'fire prevention',
      'fire fighting',
      'personal survival',
      'proficiency in survival craft',
      'elementary first aid',
      'medical first aid',
      'medical care',
      'security awareness',
      'designated security',
      'ship security officer',
      'ssr'
    ]
  },
  MEDICAL: {
    id: 'medical',
    label: 'Medical Certificates',
    icon: '🏥',
    patterns: [
      'eng1',
      'ml5',
      'medical',
      'medical fitness',
      'seafarer medical'
    ]
  },
  CERTIFICATES_OF_COMPETENCY: {
    id: 'coc',
    label: 'Certificates of Competency',
    icon: '📜',
    patterns: [
      'master',
      'chief officer',
      'officer of the watch',
      'chief engineer',
      'second engineer',
      'engineer officer',
      'boatmaster',
      'coxswain',
      'certificate of competency',
      'coc'
    ]
  },
  RADIO: {
    id: 'radio',
    label: 'Radio Certificates',
    icon: '📡',
    patterns: [
      'gmdss',
      'radio',
      'vhf',
      'src',
      'lrc',
      'roc',
      'marine radio'
    ]
  },
  ENGINEERING: {
    id: 'engineering',
    label: 'Engineering Certificates',
    icon: '⚙️',
    patterns: [
      'meol',
      'smeol',
      'engine',
      'marine engine',
      'diesel engine',
      'refrigeration',
      'oily water separator'
    ]
  },
  RYA: {
    id: 'rya',
    label: 'RYA Qualifications',
    icon: '⛵',
    patterns: [
      'rya',
      'yachtmaster',
      'powerboat',
      'day skipper',
      'coastal skipper',
      'offshore'
    ]
  },
  SPECIALIST: {
    id: 'specialist',
    label: 'Specialist Certificates',
    icon: '🎓',
    patterns: [
      'dangerous goods',
      'tanker',
      'passenger ship',
      'crowd management',
      'crisis management',
      'ro-ro',
      'dynamic positioning',
      'dp',
      'ecdis',
      'bridge resource management',
      'brm',
      'leadership',
      'fast rescue boat',
      'frb'
    ]
  },
  OTHER: {
    id: 'other',
    label: 'Other Certificates',
    icon: '📋',
    patterns: [] // Catch-all
  }
};

// ============================================================================
// CATEGORIZATION FUNCTIONS
// ============================================================================

/**
 * Categorize a certificate based on its title and type
 * @param {Object} certificate - Certificate object with title and type
 * @returns {string} Category ID (e.g., 'stcw', 'medical', 'other')
 */
export function categorizeCertificate(certificate) {
  if (!certificate) return 'other';

  const searchText = `${certificate.title || ''} ${certificate.type || ''} ${certificate.name || ''}`.toLowerCase();

  // Check each category's patterns
  for (const [key, category] of Object.entries(CERTIFICATE_CATEGORIES)) {
    if (key === 'OTHER') continue; // Skip catch-all

    const matches = category.patterns.some(pattern =>
      searchText.includes(pattern.toLowerCase())
    );

    if (matches) return category.id;
  }

  return 'other'; // Default catch-all
}

/**
 * Process certificates into categorized groups with stats
 * @param {Array} certificates - Array of certificate objects
 * @returns {Object} Processed data with categories and stats
 */
export function processCertificates(certificates) {
  if (!Array.isArray(certificates)) {
    return {
      categories: {},
      stats: {
        total: 0,
        valid: 0,
        expiringSoon: 0,
        expired: 0,
        trainingIssued: 0
      },
      expiredCerts: []
    };
  }

  // Initialize category groups
  const categories = {};
  Object.values(CERTIFICATE_CATEGORIES).forEach(cat => {
    categories[cat.id] = {
      ...cat,
      certificates: [],
      count: 0,
      hasExpired: false,
      hasExpiringSoon: false
    };
  });

  // Initialize stats
  const stats = {
    total: certificates.length,
    valid: 0,
    expiringSoon: 0,
    expired: 0,
    trainingIssued: 0
  };

  const expiredCerts = [];

  // Process each certificate
  certificates.forEach(cert => {
    const categoryId = categorizeCertificate(cert);
    const expiryStatus = calculateExpiryStatus(cert.expiry_date || cert.expiryDate);

    // Add to category
    categories[categoryId].certificates.push({
      ...cert,
      expiryStatus
    });
    categories[categoryId].count++;

    // Update category flags
    if (expiryStatus === 'expired') {
      categories[categoryId].hasExpired = true;
      expiredCerts.push(cert);
    }
    if (expiryStatus === 'critical' || expiryStatus === 'warning') {
      categories[categoryId].hasExpiringSoon = true;
    }

    // Update stats
    if (cert.isFromTraining || cert.source === 'training') {
      stats.trainingIssued++;
    }

    switch (expiryStatus) {
      case 'expired':
        stats.expired++;
        break;
      case 'critical':
      case 'warning':
        stats.expiringSoon++;
        break;
      case 'valid':
        stats.valid++;
        break;
      default:
        // 'none' - no expiry date, count as valid
        stats.valid++;
    }
  });

  // Remove empty categories
  const filteredCategories = {};
  Object.entries(categories).forEach(([id, cat]) => {
    if (cat.count > 0) {
      filteredCategories[id] = cat;
    }
  });

  return {
    categories: filteredCategories,
    stats,
    expiredCerts
  };
}

// ============================================================================
// STATUS CALCULATION FUNCTIONS
// ============================================================================

/**
 * Calculate expiry status for a certificate
 * @param {string|number|null} expiryDate - ISO date string, epoch ms, or null
 * @returns {string} Status: 'expired' | 'critical' | 'warning' | 'valid' | 'none'
 */
export function calculateExpiryStatus(expiryDate) {
  if (!expiryDate) return 'none';

  const now = new Date();
  let expiry;

  // Handle epoch ms (number) or date string
  if (typeof expiryDate === 'number') {
    expiry = new Date(expiryDate);
  } else {
    expiry = new Date(expiryDate);
  }

  // Check for invalid date
  if (isNaN(expiry.getTime())) return 'none';

  const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

  if (daysUntilExpiry < 0) return 'expired';
  if (daysUntilExpiry < 30) return 'critical';
  if (daysUntilExpiry < 90) return 'warning';
  return 'valid';
}

/**
 * Get days until expiry (for display)
 * @param {string|number|null} expiryDate - ISO date string, epoch ms, or null
 * @returns {number|null} Days until expiry, or null if no expiry
 */
export function getDaysUntilExpiry(expiryDate) {
  if (!expiryDate) return null;

  const now = new Date();
  let expiry;

  // Handle epoch ms (number) or date string
  if (typeof expiryDate === 'number') {
    expiry = new Date(expiryDate);
  } else {
    expiry = new Date(expiryDate);
  }

  // Check for invalid date
  if (isNaN(expiry.getTime())) return null;

  return Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
}

/**
 * Get status display configuration
 * @param {string} status - Status from calculateExpiryStatus
 * @returns {Object} Display config with label, color classes, icon
 */
export function getStatusDisplay(status) {
  const configs = {
    expired: {
      label: 'Expired',
      bgColor: 'bg-red-50',
      textColor: 'text-red-600',
      borderColor: 'border-red-200',
      icon: '❌'
    },
    critical: {
      label: 'Expiring Soon',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-600',
      borderColor: 'border-amber-200',
      icon: '⚠️'
    },
    warning: {
      label: 'Expiring',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-600',
      borderColor: 'border-yellow-200',
      icon: '⏰'
    },
    valid: {
      label: 'Valid',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      borderColor: 'border-green-200',
      icon: '✓'
    },
    none: {
      label: 'No Expiry',
      bgColor: 'bg-gray-50',
      textColor: 'text-gray-600',
      borderColor: 'border-gray-200',
      icon: '—'
    }
  };

  return configs[status] || configs.none;
}

// ============================================================================
// FORMATTING FUNCTIONS
// ============================================================================

/**
 * Format certificate date for display
 * @param {string|number|null} date - ISO date string, epoch ms, or null
 * @returns {string} Formatted date or 'No expiry'
 */
export function formatCertificateDate(date) {
  if (!date) return 'No expiry';

  try {
    let d;
    if (typeof date === 'number') {
      d = new Date(date);
    } else {
      d = new Date(date);
    }

    if (isNaN(d.getTime())) return 'Invalid date';

    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch (error) {

    return 'Invalid date';
  }
}

/**
 * Format expiry text with days remaining
 * @param {string|number|null} expiryDate - ISO date string, epoch ms, or null
 * @returns {string} Formatted text (e.g., "Expires in 45 days" or "Expired 10 days ago")
 */
export function formatExpiryText(expiryDate) {
  const days = getDaysUntilExpiry(expiryDate);

  if (days === null) return 'No expiry';
  if (days < 0) return `Expired ${Math.abs(days)} days ago`;
  if (days === 0) return 'Expires today';
  if (days === 1) return 'Expires tomorrow';
  return `Expires in ${days} days`;
}

/**
 * Truncate certificate name for mobile
 * @param {string} name - Certificate name
 * @param {number} maxLength - Max length (default 40)
 * @returns {string} Truncated name with ellipsis if needed
 */
export function truncateCertificateName(name, maxLength = 40) {
  if (!name) return '';
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength - 3) + '...';
}

/**
 * Get a display name for a certificate (handles various field names)
 * @param {Object} cert - Certificate object
 * @param {number} index - Index (for fallback naming)
 * @returns {string} Display name
 */
export function getCertificateDisplayName(cert, index = 0) {
  return cert.type || cert.title || cert.name || cert.certificateType ||
    (cert.number ? `Certificate #${cert.number}` : `Certificate ${index + 1}`);
}
